import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
// material
import { Box, Button, Popover, Stack } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import jsPDF from 'jspdf';
import { ReactComponent as AddIcon } from '../../../../public/static/dashboard/AddIcon.svg';

import AlertDialog from '../../HelperComponents/AlertDialog';
import AddUserToOrganizationModal from './AddUserToOrganizationModal';
import { ExportDataModal } from '../app';
import { useAuth } from '../../customContextProviders/AuthContext';
import { APP_CONSTANTS } from '../../../URLConstants';
import AddLanguageToOrganizationModal from './AddLanguageToOrganizationModal';
import { getDateINYMD } from '../../../utils/formatTime';
import {
  ExportMerchantOnboardingReportWithFilter,
  ExportMerchantReportWithFilter,
  GetInactiveMerchantReport,
  MerchantBulkUpload
} from '../../../network/Merchants';
import { GetInactiveDeviceReport } from '../../../network/Device';
import {
  computeHierarchyMeta,
  parseValidationErrorMessage,
  parseValidationErrorMessageForBulkUpload
} from '../../../utils/Utilities';
import AddZoneModal from './AddZoneModal';
import AddRegionModal from '../../../hierarchyManagement/components/AddRegionModal';
import AddBranchModal from '../../../hierarchyManagement/components/AddBranchModal';
import AddUserToNoxModal from './AddUserToNoxModal';
import {
  NewNoxDeviceRequestFormModal,
  IOBDeviceRequestModal,
  UjnDeviceRequestModal,
  KagbDeviceRequestModal,
  BomDeviceRequestFormModal,
  NewNpstDeviceRequestFormModal,
  NewRsbDeviceRequestFormModal,
  GBCBDeviceRequestModal
} from '../merchantforms';
import { SnackBarAlert } from '../../HelperComponents';
import { ExportUsers } from '../../../network/User';
import HastiDeviceRequestModal from '../merchantforms/NewHastiDeviceRequestFormModal';

// ----------------------------------------------------------------------

DeviceOrderdButtons.propTypes = {
  onAction: PropTypes.func,
  organizationData: PropTypes.object,
  onDownloadExcel: PropTypes.func,
  isDownloadingExcel: PropTypes.bool
};

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});

export default function DeviceOrderdButtons({
  onAction,
  organizationData,
  onUserCreation,
  onDownloadExcel,
  isDownloadingExcel
}) {
  const location = useLocation();
  const {
    sessionUserData,
    zonesList,
    regionsList,
    headOfficesList,
    fetchZonesList,
    fetchHeadOfficesList,
    fetchRegionList,
    branchesList,
    fetchBranchesList,
    accountDetails,
    fetchTaxonomyDetails,
    userLoginOrg,
    isCheckerMakerRequired,
    fetchLanguages,
    languageList
  } = useAuth();

  const { hierarchyAdminRole } = useMemo(
    () => computeHierarchyMeta(accountDetails?.path, sessionUserData?.user?.role),
    [accountDetails?.path, sessionUserData?.user?.role]
  );

  const shouldHideAddZone = ['ZOAdmin', 'ROAdmin'].includes(hierarchyAdminRole);
  const shouldHideAddRegion = ['ROAdmin'].includes(hierarchyAdminRole);

  const DEVICE_REQUEST_COMPONENTS = {
    [APP_CONSTANTS?.CBI_CLIENT_IDENTIFIER]: NewNoxDeviceRequestFormModal,
    [APP_CONSTANTS?.IOB_CLIENT_IDENTIFIER]: IOBDeviceRequestModal,
    [APP_CONSTANTS?.RSB_CLIENT_IDENTIFIER]: NewRsbDeviceRequestFormModal,
    [APP_CONSTANTS?.UJN_CLIENT_IDENTIFIER]: UjnDeviceRequestModal,
    [APP_CONSTANTS?.KAGB_CLIENT_IDENTIFIER]: KagbDeviceRequestModal,
    [APP_CONSTANTS?.NPST_CLIENT_IDENTIFIER]: NewNpstDeviceRequestFormModal,
    [APP_CONSTANTS?.SBI_CLIENT_IDENTIFIER]: NewNoxDeviceRequestFormModal,
    [APP_CONSTANTS?.GBCB_CLIENT_IDENTIFIER]: GBCBDeviceRequestModal,
    [APP_CONSTANTS?.HASTI_CLIENT_IDENTIFIER]: HastiDeviceRequestModal
  };

  const DeviceRequestModal = DEVICE_REQUEST_COMPONENTS[userLoginOrg] || null;

  const [anchorEl, setAnchorEl] = useState(null);

  const [bulkUploadError, setBulkUploadError] = useState({});

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    fetchHeadOfficesList();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const loadRegionFormHierarchyData = () => {
    fetchHeadOfficesList();
    fetchZonesList();
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'add-dropdown-popover' : undefined;
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [alertDialogMetas, setAlertDialogMetas] = useState({
    message: '',
    title: '',
    btnLabel: ''
  });

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);
  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [isDeviceRequestModalOpen, setIsDeviceRequestModalOpen] = useState(false);
  const [isAddUserToOrganizationModalOpen, setIsAddUserToOrganizationModalOpen] = useState(false);
  const [isAddUserToNoxModalOpen, setIsAddUserToNoxModalOpen] = useState(false);
  const [isAddLanguageToOrganizationOpen, setIsAddLanguageToOrganizationOpen] = useState(false);
  const [isCreateZoneModalOpen, setIsCreateZoneModalOpen] = useState(false);
  const [isCreateRegionModalOpen, setIsCreateRegionModalOpen] = useState(false);
  const [isCreateBranchModalOpen, setIsCreateBranchModalOpen] = useState(false);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);
  const [requestProcessing, setRequestProcessing] = useState({
    merchantExport: false,
    data: null
  });

  function onNewDeviceRequestModalAction(d) {
    alert(d);
  }
  // Add the state for the export popover in the main functional component.
  const [exportAnchorEl, setExportAnchorEl] = useState(null);

  // Function to handle ExportPopOver opening
  const handleExportOpen = (event) => {
    setExportAnchorEl(event.currentTarget);
  };

  // Function to close the ExportPopOver
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  // Determine if popover is open
  const exportOpen = Boolean(exportAnchorEl);
  const exportPopoverId = exportOpen ? 'export-dropdown-popover' : undefined;

  function onAddLanguageToOrganizationModalAction(d) {
    alert(d);
  }

  function onExportDataFilterModalAction(filterPayload) {
    if (
      filterPayload?.startDate === undefined ||
      filterPayload?.endDate === undefined ||
      filterPayload?.startDate === null ||
      filterPayload?.endDate === null
    ) {
      setSnackBarMetas((prevState) => ({
        ...prevState,
        title: "You haven't selected the Date Range.",
        severity: 'warning'
      }));
      setIsSnackBarAlertOpen(true);

      return false;
    }
    requestMerchantReportExportWithFilter(filterPayload);
  }

  async function requestMerchantReportExportWithFilter(filterPayload) {
    const payload = {
      startDate: getDateINYMD(filterPayload?.startDate),
      endDate: getDateINYMD(filterPayload?.endDate)
    };

    setRequestProcessing((p) => ({ ...p, merchantExport: true }));

    try {
      let res;
      let defaultFileName;

      switch (filterPayload?.actionData) {
        case APP_CONSTANTS.INACTIVE_MERCHANTS_REPORT:
          res = await GetInactiveMerchantReport('ACTIVE', payload.startDate, payload.endDate);
          defaultFileName = `${payload.startDate}_${payload.endDate}_InactiveMerchantsReport.csv`;
          break;

        case APP_CONSTANTS.INACTIVE_DEVICES_REPORT:
          res = await GetInactiveDeviceReport(payload.startDate, payload.endDate);
          defaultFileName = `${payload.startDate}_${payload.endDate}_InactiveDevicesReport.csv`;
          break;

        case APP_CONSTANTS.TRANSACTION_EXPORT_REPORT:
          res = await ExportMerchantReportWithFilter(payload.startDate, payload.endDate);
          defaultFileName = `${payload.startDate}_${payload.endDate}_TransactionReport.csv`;
          break;

        default:
          // MERCHANT_ONBOARDING_EXPORT_REPORT
          res = await ExportMerchantOnboardingReportWithFilter(payload.startDate, payload.endDate);
          defaultFileName = `${payload.startDate}_${payload.endDate}_MerchantOnboardingReport.csv`;
          break;
      }

      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      console.log('content-disposition', res.headers['content-disposition']);

      const contentDisposition = res.headers['content-disposition'];
      let fileName = defaultFileName;

      if (contentDisposition) {
        let match = contentDisposition.match(/filename\*=UTF-8''(.+)/);

        if (match) {
          const [, extractedName] = match;
          if (extractedName) {
            fileName = decodeURIComponent(extractedName);
          }
        } else {
          match = contentDisposition.match(/filename="(.+)"/);

          if (match) {
            const [, extractedName] = match;
            if (extractedName) {
              fileName = extractedName;
            }
          }
        }
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);

      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setSnackBarMetas((prev) => ({
        ...prev,
        title: 'File has been downloaded in your system',
        severity: 'success'
      }));
      setIsSnackBarAlertOpen(true);
    } catch (err) {
      setSnackBarMetas((prev) => ({
        ...prev,
        title: parseValidationErrorMessage(err),
        severity: 'error'
      }));
      setIsSnackBarAlertOpen(true);
    } finally {
      setRequestProcessing((p) => ({ ...p, merchantExport: false }));
    }
  }

  const fileInputRef = useRef(null);

  const handleFileUpload = (event) => {
    setBulkUploadLoading(true);
    MerchantBulkUpload(event.target.files[0])
      .then((_res) => {
        setBulkUploadLoading(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setSnackBarMetas((prevState) => ({
          ...prevState,
          title: 'Merchants have been successfully created !',
          severity: 'success'
        }));
        setIsSnackBarAlertOpen(true);
      })
      .catch((error) => {
        setBulkUploadLoading(false);

        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setAlertDialogMetas({
          ...alertDialogMetas,
          message: parseValidationErrorMessageForBulkUpload(error),
          title: 'Error processing the file',
          btnLabel: 'OK'
        });
        setBulkUploadError(parseValidationErrorMessageForBulkUpload(error));
        setOpenAlertDialog(true);
      });
  };

  function exportErrorReport() {
    // eslint-disable-next-line new-cap
    const doc = new jsPDF();
    const logoUrl = 'https://i.postimg.cc/FKLr13w7/logo.png';
    const img = new Image();
    img.src = logoUrl;
    img.onload = function () {
      // Add the logo (X: 10, Y: 5, Width: 15, Height: 15)
      // doc.addImage(img, 'PNG', 10, 5, 10, 10);

      // Get the current date and time
      const now = new Date();
      const formattedDate = now.toLocaleDateString();
      const formattedTime = now.toLocaleTimeString();

      // Add the title with the current date and time
      doc.setFont('Helvetica', 'bold');
      doc.text(10, 11, `Error Report`);

      // Draw a border around the content
      // doc.rect(5, 20, 200, 135); // X, Y, Width, Height

      // Report content inside the box
      doc.setFont('Mulish', 'normal');
      doc.text(10, 20, `${formattedDate} ${formattedTime}`);

      const errorMessage =
        typeof bulkUploadError === 'string'
          ? bulkUploadError
          : JSON.stringify(bulkUploadError, null, 2);

      // Define the maximum width for the text
      const maxWidth = 190; // Adjust this value as needed
      const textX = 10;
      const textY = 30;

      // Add the text with wrapping
      doc.text(errorMessage || 'NA', textX, textY, { maxWidth });

      doc.save(`error_report_${formattedDate}_${formattedTime}.pdf`);
      setOpenAlertDialog(false);
    };
  }

  const handleExportUsers = async () => {
    try {
      const response = await ExportUsers();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Users.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      setSnackBarMetas((prev) => ({
        ...prev,
        title: 'File has been downloaded in your system',
        severity: 'success'
      }));
      setIsSnackBarAlertOpen(true);
    } catch (err) {
      setSnackBarMetas((prev) => ({
        ...prev,
        title: parseValidationErrorMessage(err),
        severity: 'error'
      }));
      setIsSnackBarAlertOpen(true);
    }
  };

  return (
    <Box>
      <Box sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between">
          {/* Left aligned buttons */}
          <Stack direction="row">
            {sessionUserData?.user?.role !== APP_CONSTANTS.USER_ROLE_CHECKER &&
              (location?.pathname === '/dashboard/soundboxT' ||
                location?.pathname === '/dashboard/merchants') && (
                <Button
                  data-cy="new-merchant-request"
                  variant="contained"
                  color="whitebg"
                  size="small"
                  sx={{ mr: 1, minHeight: '4vh', minWidth: 110 }}
                  onClick={() => {
                    setIsDeviceRequestModalOpen(true);
                    fetchTaxonomyDetails(sessionUserData?.user?.taxonomyId);
                    fetchRegionList();
                    fetchLanguages();

                    if (
                      process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID ===
                      APP_CONSTANTS.CBI_CLIENT_IDENTIFIER &&
                      sessionUserData?.user?.taxonomyId
                    ) {
                      fetchBranchesList();
                    }
                    if (
                      process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID ===
                      APP_CONSTANTS.IOB_CLIENT_IDENTIFIER
                    ) {
                      fetchBranchesList();
                    }
                    if (
                      process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID ===
                      APP_CONSTANTS.GBCB_CLIENT_IDENTIFIER
                    ) {
                      fetchBranchesList();
                    }
                  }}
                >
                  New Merchant Request
                </Button>
              )}

            {process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID ===
              APP_CONSTANTS?.BOM_CLIENT_IDENTIFIER &&
              location?.pathname === '/dashboard/soundboxT' && (
                <LoadingButton
                  loading={bulkUploadLoading}
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  color="whitebg"
                  component="label"
                >
                  Bulk Merchant Request
                  <VisuallyHiddenInput
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    type="file"
                    accept=".csv"
                  />
                </LoadingButton>
              )}

            {sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_SUPER_ADMIN ? (
              <Button
                variant="contained"
                color="whitebg"
                size="small"
                sx={{ minHeight: '4vh', ml: 2, minWidth: 120 }}
                onClick={() => setIsAddLanguageToOrganizationOpen(true)}
              >
                Add New Language
              </Button>
            ) : null}

            {
              location?.pathname === '/dashboard/soundboxT' ? (
                <>
                  <LoadingButton
                    loading={requestProcessing?.merchantExport}
                    variant="contained"
                    color="whitebg"
                    size="small"
                    sx={{ minHeight: '4vh', mx: 1, minWidth: 120 }}
                    onClick={handleExportOpen}
                    startIcon={<FileDownloadOutlinedIcon />}
                    endIcon={<KeyboardArrowDownRoundedIcon />}
                  >
                    Export Data
                  </LoadingButton>
                  {/* <Button
                  variant="contained"
                  color="whitebg"
                  size="small"
                  sx={{ minHeight: '4vh', ml: 1, minWidth: 120 }}
                  onClick={handleExportOpen}
                  startIcon={<AddIcon />} // You can customize the icon here
                  endIcon={<KeyboardArrowDownRoundedIcon />}
                >
                  Export
                </Button> */}

                  <Popover
                    id={exportPopoverId}
                    sx={{
                      '& .MuiPopover-paper': {
                        p: 2,
                        mt: 1
                      }
                    }}
                    open={exportOpen}
                    anchorEl={exportAnchorEl}
                    onClose={handleExportClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                  >
                    <div>
                      <Button
                        sx={{
                          minWidth: 150,
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: '#e0e0e0' // darker grey on hover
                          }
                        }}
                        onClick={() => {
                          handleExportClose();
                          setIsTransactionOpen(true);
                          setRequestProcessing({
                            ...requestProcessing,
                            data: APP_CONSTANTS.TRANSACTION_EXPORT_REPORT
                          });
                        }}
                      >
                        Transaction History
                      </Button>
                    </div>
                    <div>
                      <Button
                        sx={{
                          minWidth: 150,
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: '#e0e0e0' // darker grey on hover
                          }
                        }}
                        onClick={() => {
                          handleExportClose();
                          setIsTransactionOpen(true);
                          setRequestProcessing({
                            ...requestProcessing,
                            data: APP_CONSTANTS.MERCHANT_ONBOARDING_EXPORT_REPORT
                          });
                        }}
                      >
                        Merchant Onboarding History
                      </Button>
                    </div>
                    <div>
                      <Button
                        sx={{
                          minWidth: 150,
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: '#e0e0e0' // darker grey on hover
                          }
                        }}
                        onClick={() => {
                          handleExportClose();
                          setIsTransactionOpen(true);
                          setRequestProcessing({
                            ...requestProcessing,
                            data: APP_CONSTANTS.INACTIVE_MERCHANTS_REPORT
                          });
                        }}
                      >
                        Inactive Merchants Report
                      </Button>
                    </div>
                    <div>
                      <Button
                        sx={{
                          minWidth: 150,
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: '#e0e0e0' // darker grey on hover
                          }
                        }}
                        onClick={() => {
                          handleExportClose();
                          setIsTransactionOpen(true);
                          setRequestProcessing({
                            ...requestProcessing,
                            data: APP_CONSTANTS.INACTIVE_DEVICES_REPORT
                          });
                        }}
                      >
                        Inactive Devices Report
                      </Button>
                    </div>
                  </Popover>
                </>
              ) : null}

            {isCheckerMakerRequired === 'YES' ? (
              (sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_SUPER_ADMIN ||
                sessionUserData?.user?.role === APP_CONSTANTS?.USER_ROLE_ADMIN) && (
                <>
                  <Button
                    variant="contained"
                    color="whitebg"
                    size="small"
                    sx={{ minHeight: '4vh', ml: 1, minWidth: 120 }}
                    onClick={handleOpen}
                    startIcon={<AddIcon />}
                    endIcon={<KeyboardArrowDownRoundedIcon />}
                  >
                    Add
                  </Button>

                  <Popover
                    id={popoverId}
                    sx={{
                      '& .MuiPopover-paper': {
                        p: 2,
                        mt: 1
                      }
                    }}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left'
                    }}
                  >
                    <div>
                      <Button
                        sx={{
                          minWidth: 150,
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: '#e0e0e0' // darker grey on hover
                          }
                        }}
                        onClick={() => {
                          handleClose();
                          setIsAddUserToNoxModalOpen(true);
                        }}
                      >
                        Add New User
                      </Button>
                    </div>
                    {!shouldHideAddZone && (
                      <div>
                        <Button
                          sx={{
                            minWidth: 150,
                            backgroundColor: 'transparent',
                            color: 'text.primary',
                            '&:hover': {
                              backgroundColor: '#e0e0e0' // darker grey on hover
                            }
                          }}
                          onClick={() => {
                            handleClose();
                            setIsCreateZoneModalOpen(true);
                          }}
                        >
                          Add New Zone
                        </Button>
                      </div>
                    )}
                    {!shouldHideAddRegion && (
                      <div>
                        <Button
                          sx={{
                            minWidth: 150,
                            backgroundColor: 'transparent',
                            color: 'text.primary',
                            '&:hover': {
                              backgroundColor: '#e0e0e0' // darker grey on hover
                            }
                          }}
                          onClick={() => {
                            handleClose();
                            setIsCreateRegionModalOpen(true);
                            loadRegionFormHierarchyData();
                          }}
                        >
                          Add New Region
                        </Button>
                      </div>
                    )}
                    <div>
                      <Button
                        sx={{
                          minWidth: 150,
                          backgroundColor: 'transparent',
                          color: 'text.primary',
                          '&:hover': {
                            backgroundColor: '#e0e0e0' // darker grey on hover
                          }
                        }}
                        onClick={() => {
                          handleClose();
                          setIsCreateBranchModalOpen(true);
                          fetchZonesList();
                        }}
                      >
                        Add New Branch
                      </Button>
                    </div>
                  </Popover>
                </>
              )
            ) : (
              <Button
                variant="contained"
                color="whitebg"
                size="small"
                sx={{ mr: 1, minHeight: '4vh', minWidth: 110 }}
                onClick={() => {
                  handleClose();
                  setIsAddUserToOrganizationModalOpen(true);
                  fetchHeadOfficesList();
                }}
              >
                Add New User
              </Button>
            )}

            {location?.pathname === '/dashboard/organizationusers' && (
              <Button
                variant="contained"
                color="whitebg"
                onClick={handleExportUsers}
                startIcon={<FileDownloadOutlinedIcon />}
                sx={{ minHeight: '4vh', ml: 2, minWidth: 120 }}
              >
                Export Users
              </Button>
            )}

            {(location?.pathname === '/dashboard/merchants' ||
              location?.pathname === '/dashboard/organizationusers') &&
              onDownloadExcel && (
                <LoadingButton
                  loading={isDownloadingExcel}
                  variant="contained"
                  color="whitebg"
                  size="small"
                  sx={{ ml: 2, minHeight: '4vh', minWidth: 140 }}
                  onClick={onDownloadExcel}
                  startIcon={<FileDownloadOutlinedIcon />}
                >
                  Export Current View
                </LoadingButton>
              )}
          </Stack>
        </Stack>
      </Box>
      <AlertDialog
        openAlertDialog={openAlertDialog}
        message={alertDialogMetas?.message}
        title={alertDialogMetas?.title}
        btnLabel={alertDialogMetas?.btnLabel}
        handleClose={() => setOpenAlertDialog(false)}
        bulkUploadError={bulkUploadError}
        exportErrorReport={() => exportErrorReport()}
      />
      {process.env.REACT_APP_NOTIFICATION_B2B_CLIENT_ID ===
        APP_CONSTANTS?.BOM_CLIENT_IDENTIFIER && (
          <BomDeviceRequestFormModal
            opneAssignedModal={isDeviceRequestModalOpen}
            handleClose={() => setIsDeviceRequestModalOpen(false)}
            onAction={(d) => onNewDeviceRequestModalAction(d)}
            accountDetails={accountDetails}
            branchesList={branchesList}
            fetchBranchesList={(regionalOfficeId) => fetchBranchesList(regionalOfficeId)}
            regionsList={regionsList}
          />
        )}
      {DeviceRequestModal && (
        <DeviceRequestModal
          opneAssignedModal={isDeviceRequestModalOpen}
          handleClose={() => setIsDeviceRequestModalOpen(false)}
          onAction={(d) => onNewDeviceRequestModalAction(d)}
          accountDetails={accountDetails}
          branchesList={branchesList}
          fetchBranchesList={(regionalOfficeId) => fetchBranchesList(regionalOfficeId)}
          regionsList={regionsList}
          languageList={languageList}
        />
      )}
      <ExportDataModal
        actionData={requestProcessing?.data}
        openDateRangeModal={isTransactionOpen}
        handleClose={() => setIsTransactionOpen(false)}
        onAction={(filetrPayload) => onExportDataFilterModalAction(filetrPayload)}
      />
      <AddUserToOrganizationModal
        opneAssignedModal={isAddUserToOrganizationModalOpen}
        handleClose={() => setIsAddUserToOrganizationModalOpen(false)}
        onAction={onUserCreation}
        headOfficesList={headOfficesList}
      />
      <AddUserToNoxModal
        opneAssignedModal={isAddUserToNoxModalOpen}
        handleClose={() => setIsAddUserToNoxModalOpen(false)}
        headOfficesList={headOfficesList}
        zonesList={zonesList}
        regionsList={regionsList}
        branchesList={branchesList}
        fetchRegions={(headOfficeId, zoneOfficeId) => fetchRegionList(headOfficeId, zoneOfficeId)}
        fetchZones={(headOfficeId) => fetchZonesList(headOfficeId)}
        fetchBranchesList={(regionalOfficeId) => fetchBranchesList(regionalOfficeId)}
        onAction={onUserCreation}
      />
      <AddLanguageToOrganizationModal
        organizationData={organizationData}
        opneAssignedModal={isAddLanguageToOrganizationOpen}
        handleClose={() => setIsAddLanguageToOrganizationOpen(false)}
        onAction={(d) => onAddLanguageToOrganizationModalAction(d)}
      />
      <AddZoneModal
        opneAssignedModal={isCreateZoneModalOpen}
        handleClose={() => setIsCreateZoneModalOpen(false)}
        headOfficesList={headOfficesList}
      />
      <AddRegionModal
        opneAssignedModal={isCreateRegionModalOpen}
        handleClose={() => setIsCreateRegionModalOpen(false)}
        headOfficesList={headOfficesList}
        fetchZones={(headOfficeId) => fetchZonesList(headOfficeId)}
        zonesList={zonesList}
      />
      <AddBranchModal
        opneAssignedModal={isCreateBranchModalOpen}
        handleClose={() => setIsCreateBranchModalOpen(false)}
        zonesList={zonesList}
        regionsList={regionsList}
        headOfficesList={headOfficesList}
        fetchZones={(headOfficeId) => fetchZonesList(headOfficeId)}
        fetchRegions={(headOfficeId, zoneOfficeId) => fetchRegionList(headOfficeId, zoneOfficeId)}
      />
      <SnackBarAlert
        open={isSnackBarAlertOpen}
        title={snackBarMetas?.title}
        message={snackBarMetas?.message}
        severity={snackBarMetas?.severity}
        handleClose={() => setIsSnackBarAlertOpen(false)}
      />
    </Box>
  );
}
